import React, { useEffect, useState } from "react";
import { currentUrl, unsavedChanges } from "../../App";
import { useLocation } from "react-router-dom";
import { Button, Card, Col, Container, InputGroup, Row, Spinner, Stack, Table } from "react-bootstrap";
import { fetchBedChecks, fetchBeds, fetchRooms, patchRoom, postBedCheck, updateConsecutiveCount } from "../../services/httpClient";
import PageTitle from "../ui/PageTitle";
import BedOccupancyCard from "./BedOccupancyCard";
import useApp from "../../utils/useApp";
import SelectObject from "../ui/SelectObject";

interface BedOccupancyState {
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  getData: boolean;
  errMsg: any;
  setErrMsg: any;
}

const BedOccupancy = ({ setOrgToSave, setShowToast, setToastText, setToastBody, getData, errMsg, setErrMsg }: BedOccupancyState) => {
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { showMenu }: any = useApp();
  const [filterRoom, setFilterRoom] = useState<any>();
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [missingBeds, setMissingBeds] = useState<any[]>([]);
  const [resetCards, setResetCards] = useState<boolean>(false);
  const [showMissingReport, setShowMissingReport] = useState<boolean>(false);
  const [bedChecks, setBedChecks] = useState<any[]>([]);
  const [viewOccupied, setViewOccupied] = useState<boolean>(false);
  const [viewAll, setViewAll] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      setViewOccupied(false);
      setViewAll(true);
      getBeds(false);
    }
  }, [getData]);

  const getBeds = (maintainFilter: boolean) => {
    fetchBeds()
      .then((res: any) => {
        const bedList = res[0].data;
        setBeds(bedList);
        setResetCards(false);

        bedList.sort((a: any, b: any) => {
          const roomCompare = a.room_key.localeCompare(b.room_key, undefined, { sensitivity: "base" });
          if (roomCompare !== 0) return roomCompare;

          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        getRooms(bedList, maintainFilter);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getRooms = (bedList: any, maintainFilter: boolean) => {
    fetchRooms()
      .then((res: any) => {
        let roomList = res[0].data;
        roomList = roomList.map((room: any) => {
          const bedCount = bedList.filter((bed: any) => bed.room_id === room.id).length;
          return {
            ...room,
            name: `${room.name} (${bedCount} beds)`,
          }
        });

        roomList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base'})
        });
        
        roomList = roomList.map((room: any) => {
          if (room?.occupancy_check_at) {
            const utcDate = new Date(room?.occupancy_check_at + " UTC");
            const localTime = utcDate.toLocaleString(undefined, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });
            return { ...room, occupancy_check_at: localTime };
          } else {
            return room;
          }
        });

        setRooms(roomList);

        if (maintainFilter) {
          if (filterRoom === "0") {
            setFilteredBeds(bedList);
          } else {
            const room = roomList.find((room: any) => room.key === filterRoom?.key);
            setFilterRoom(room);
            const filtered = bedList.filter((bed: any) => bed.room_id === filterRoom?.id);
            setFilteredBeds(filtered);
          }
        } else if (roomList.length === 1) {
          setFilterRoom(roomList[0]);
          const roomId = roomList[0].id;
          const filtered = bedList.filter((bed: any) => bed.room_id === roomId);
          setFilteredBeds(filtered);
        } else {
          setFilterRoom(roomList[0]);
          const roomId = roomList[0].id;
          const filtered = bedList.filter((bed: any) => bed.room_id === roomId);
          setFilteredBeds(filtered);
        }
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const filterByRoom = (e: any) => {
    if (e.target.value === "0") {
      setFilteredBeds(beds);
      setFilterRoom("0");
      return;
    }
    const room = rooms.find((room: any) => room.key === e.target.value);
    setFilterRoom(room);
    setMissingBeds([]);
    setResetCards(true);
    setViewOccupied(false);
    setViewAll(true);

    const roomId = rooms.find((room: any) => room.key === e.target.value)?.id;
    const filtered = beds.filter((bed: any) => bed.room_id === roomId);
    setFilteredBeds(filtered);
  };

  const onClickSubmit = () => {
    setIsUpdating(true);
    setErrMsg("");
    let failedBeds: any[] = [];

    const updatedRoom = {
      occupancy_check: true,
    };

    // All Rooms
    if (filterRoom === "0") {
      Promise.all(
        rooms.map((room: any) =>
          patchRoom(updatedRoom, room.id)
            .then(async () => {
              const roomMissingBeds = missingBeds.filter((b: any) => b.room_id === room.id);

              await Promise.allSettled(
                roomMissingBeds.map((m: any, index: number) => {
                  const bedObject = {
                    room_id: m?.room_id,
                    bed_id: m?.id,
                    occupant_id: m?.occupant_id,
                    occupant_type: m?.occupant_type,
                  };

                  return postBedCheck(bedObject)
                    .catch(() => {
                      failedBeds.push(m);
                    });
                })
              );
            })
            .catch((err: any) => {
              console.log(`Failed to patch room ${room.name}:`, err?.response?.data?.msg);
            })
        )
      )
        .then(async () => {
          await updateFailedConsecutiveCount(failedBeds);

          setShowToast(true);
          setToastText("Bed Occupancy Submitted");
          setToastBody(missingBeds.length > 0 ? `${missingBeds.length} Missing` : "No Missing");

          unsavedChanges.value = false;
          setOrgToSave(false);
          setMissingBeds([]);
          setResetCards(true);
          setViewOccupied(false);
          setViewAll(true);
          getBeds(true);
          setIsUpdating(false);

          // Error messaging
          if (failedBeds.length === 1) {
            const failedBedsText = failedBeds.map(note => note.name);
            setErrMsg("Failed to mark missing for bed: " + failedBedsText);
          } else if (failedBeds.length > 0) {
            const failedBedsText = failedBeds.map(note => note.name).join("\n");
            setErrMsg("Failed to mark missing for beds: \n" + failedBedsText);
          }
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsUpdating(false);
        });

      return;
    }

    patchRoom(updatedRoom, filterRoom?.id)
      .then(async () => {
        if (missingBeds.length > 0) {
          await Promise.allSettled(
            missingBeds.map((m: any, index: number) => {
              const bedObject = {
                room_id: m?.room_id,
                bed_id: m?.id,
                occupant_id: m?.occupant_id,
                occupant_type: m?.occupant_type,
              };

              return postBedCheck(bedObject)
                .then(async () => {
                  if (missingBeds.length === index + 1) {
                    await updateFailedConsecutiveCount(failedBeds);
                    setShowToast(true);
                    setToastText("Bed Occupancy Submitted");
                    setToastBody(missingBeds.length + " Missing");
                    unsavedChanges.value = false;
                    setOrgToSave(false);
                    setMissingBeds([]);
                    setResetCards(true);
                    setViewOccupied(false);
                    setViewAll(true);
                    getBeds(true);
                  }
                })
                .catch((err: any) => {
                  failedBeds.push(m);
                });
            })
          );
          setIsUpdating(false);
          // Return the list of failed bed names as an error
          if (failedBeds.length === 1) {
            const failedBedsText = failedBeds.map(note => note.name);
            setErrMsg('Failed to mark missing for bed: ' + failedBedsText);
          } else if (failedBeds.length > 0) {
            const failedBedsText = failedBeds.map(note => note.name).join('\n');
            setErrMsg('Failed to mark missing for beds: \n' + failedBedsText);
            return;
          }
        } else {
          await updateFailedConsecutiveCount(failedBeds);
          setShowToast(true);
          setToastText("Bed Occupancy Submitted");
          setToastBody("No Missing");
          unsavedChanges.value = false;
          setOrgToSave(false);
          setMissingBeds([]);
          setResetCards(true);
          getBeds(true);
          setIsUpdating(false);
        }
      })
      .catch((err: any) => {
        console.log(err?.response?.data?.msg);
        setErrMsg(err?.response?.data?.msg + ": " + filterRoom.name);
        setIsUpdating(false);
        return;
      });
  };

  const updateFailedConsecutiveCount = async (failedBeds: any[]) => {
    const occupiedBeds = filteredBeds.filter((b: any) => b.occupant_id);
    await Promise.allSettled(
      occupiedBeds.map((m: any, index: number) => {
        const bedObject = {
          is_missing: missingBeds.some((b: any) => b.id === m.id) && !failedBeds.some((b: any) => b.id === m.id) ? 1 : 0,
          room_id: m?.room_id,
          bed_id: m?.id,
          occupant_id: m?.occupant_id,
          occupant_type: m?.occupant_type,
        };

        return updateConsecutiveCount(bedObject)
          .catch(() => {
            setErrMsg("Failed to update consecutive count for bed: " + m.name);
          });
      })
    );
  }

  const onClickCancel = () => {
    setErrMsg("");
    unsavedChanges.value = false;
    setOrgToSave(false);
    setMissingBeds([]);
    setResetCards(true);
    setViewOccupied(false);
    setViewAll(true);
    getBeds(true);
  };

  const onClickViewReport = () => {
    setMissingBeds([]);
    fetchBedChecks()
      .then((res: any) => {
        const bedList = res[0].data.map((bed: any) => {
          const utcDate = new Date(bed?.created_at + " UTC");
          const localTime = utcDate.toLocaleString(undefined, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
          return { ...bed, created_at: localTime };
        });

        setBedChecks(bedList);
        setShowMissingReport(true);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onClickViewCheck = () => {
    setShowMissingReport(false);
    setMissingBeds([]);
  };

  const onClickViewOccupied = () => {
    setViewOccupied(true);
    setViewAll(false);
    const filtered = beds.filter((bed: any) => bed.room_id === filterRoom?.id);
    const occupiedBeds = filtered.filter((b: any) => b.occupant_id);
    setFilteredBeds(occupiedBeds);
  };

  const onClickViewAll = () => {
    setViewOccupied(false);
    setViewAll(true);
    const filtered = beds.filter((bed: any) => bed.room_id === filterRoom?.id);
    setFilteredBeds(filtered);
  };

  return showMissingReport === true ? (
    <>
      <PageTitle
        title="Bed Occupancy Report"
        type="section"
        buttonColor="primary"
        buttonTitle="Done"
        onButtonClick={() => onClickViewCheck()}
      />
      <Row className="pb-5 mt-2">
        <Col xs={12}>
          <Row>
            <h5 className="app-header-text">Missing Report Last 7 Days</h5>
          </Row>
          {bedChecks.length > 0 ? (
            <>
              <Table striped>
                <thead>
                  <tr className="text-muted">
                    <th>Date</th>
                    <th>Room Name</th>
                    <th>Bed Name</th>
                    <th>Occupant Name</th>
                    <th>Consecutive</th>
                    <th>Submitted By</th>
                  </tr>
                </thead>
                <tbody>
                  {bedChecks?.map((m: any) => (
                    <tr key={m.id}>
                      <td>{m.created_at}</td>
                      <td>{m.room_name}</td>
                      <td>{m.name}</td>
                      <td>{m.firstname + " " + m.lastname}</td>
                      <td>{m.consecutive}</td>
                      <td>{m.created_firstname + " " + m.created_lastname}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          ) : (
            <p className="mt-2">No Missing Reported</p>
          )}
        </Col>
      </Row>
    </>
  ) : (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg.split('\n').map((line: any, index: any) => (
              <span key={index}>
                {line}
                <br />
              </span>
            ))}
          </p>
          <>
            <PageTitle
              title="Bed Occupancy Check"
              type="section"
              buttonColor="primary"
              buttonTitle="Occupancy Report"
              onButtonClick={() => onClickViewReport()}
            />
            <Row className={showMenu ? "mt-3 sticky-top card-note-menu-open-sticky" : "mt-3 sticky-top card-note-sticky"}>
              <Col xs={12} lg={10}>
                <div style={{ overflow: "hidden", backgroundColor: "#fff" }}>
                  <Card className="bg-light">
                    <Row className="pe-0 mb-3 mt-3 ms-1 me-1">
                      <Col xs={6}>
                        <SelectObject
                          label="Select a Room"
                          value={filterRoom?.key}
                          options={rooms}
                          onUpdate={(e: any) => filterByRoom(e)}
                          initialOption={"View All"}
                          disabled={missingBeds.length > 0 ? true : false}
                        />
                      </Col>
                      <Col xs={6} className="d-flex justify-content-end align-items-center">
                        <InputGroup className="d-flex justify-content-end align-items-center">
                          <Button
                            className={
                              viewOccupied ? "app-primary-color me-2 button rounded" : "app-primary-color-inverse me-2 button rounded"
                            }
                            onClick={() => onClickViewOccupied()}
                          >
                            Occupied
                          </Button>
                          <Button
                            className={viewAll ? "app-primary-color me-2 button rounded" : "app-primary-color-inverse me-2 button rounded"}
                            onClick={() => onClickViewAll()}
                          >
                            All
                          </Button>
                        </InputGroup>
                      </Col>
                    </Row>
                  </Card>
                </div>
                {filteredBeds.length > 0 && (
                  <Card className="border-0 rounded-0">
                    <Row className="pe-0 mb-3 mt-3 ms-1 me-1">
                      <Col xs={12} className="d-flex justify-content-start align-items-center">
                        <Stack direction="horizontal" gap={3}>
                          <Button className="app-secondary-color button" onClick={() => onClickSubmit()} disabled={isUpdating}>
                            {missingBeds.length > 0 ? "Submit" : "Submit with No Missing"}
                          </Button>
                          <div className="vr" />
                          <Button
                            className="app-primary-color button"
                            onClick={() => onClickCancel()}
                            disabled={missingBeds.length > 0 ? false : true}
                          >
                            Cancel
                          </Button>
                          {filterRoom !== "0" && (
                            <p className="app-header-text m-0 p-0">
                              {filterRoom?.occupancy_check_at
                                ? "Last Checked " + filterRoom?.occupancy_check_at
                                : "No Occupancy Check Completed"}{" "}
                              {filterRoom?.firstname && filterRoom?.lastname
                                ? "By " + filterRoom?.firstname + " " + filterRoom?.lastname
                                : ""}{" "}
                            </p>
                          )}
                        </Stack>
                      </Col>
                    </Row>
                  </Card>
                )}
              </Col>
            </Row>
            <Row className="pb-3 mt-0">
              <Col xs={12} lg={10} className="pb-3">
                {filteredBeds.length === 0 ? (
                  <Row className="mt-3">
                    <p className="mt-2">No Beds Created</p>
                  </Row>
                ) : (
                  filteredBeds?.map((item: any, index: number) => (
                    <Row key={index}>
                      <Col xs={12}>
                        <Row className="mb-1"></Row>
                        <BedOccupancyCard
                          item={item}
                          missingBeds={missingBeds}
                          setMissingBeds={setMissingBeds}
                          resetCards={resetCards}
                          setOrgToSave={setOrgToSave}
                        />
                      </Col>
                    </Row>
                  ))
                )}
              </Col>
            </Row>
          </>
        </>
      )}
    </>
  );
};

export default BedOccupancy;
